// Avatar Service - Download and store avatar images
import { supabase } from './supabase';

const AVATAR_BUCKET = 'avatars';

/**
 * Load an image and convert it to a blob using canvas
 * This bypasses CORS issues by drawing to canvas
 * @param imageUrl - The image URL to load
 * @returns A promise that resolves to a Blob, or null on failure
 */
async function loadImageAsBlob(imageUrl: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('Could not get canvas context');
          resolve(null);
          return;
        }

        ctx.drawImage(img, 0, 0);

        // Convert to blob
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.9);
      } catch (err) {
        console.error('Canvas error:', err);
        resolve(null);
      }
    };

    img.onerror = (err) => {
      console.error('Image load error:', err);
      resolve(null);
    };

    // Add cache-busting to avoid CORS caching issues
    const separator = imageUrl.includes('?') ? '&' : '?';
    img.src = `${imageUrl}${separator}t=${Date.now()}`;
  });
}

/**
 * Download an image from a URL and store it in Supabase Storage
 * @param userId - The user's ID for organizing storage
 * @param imageUrl - The source URL (e.g., Google profile picture)
 * @param filename - Optional custom filename
 * @returns The Supabase Storage public URL, or null on failure
 */
export async function downloadAndStoreAvatar(
  userId: string,
  imageUrl: string,
  filename: string = 'avatar.jpg'
): Promise<string | null> {
  try {
    // Try canvas approach first (handles CORS better)
    let blob = await loadImageAsBlob(imageUrl);

    // If canvas fails, try direct fetch as fallback
    if (!blob) {
      console.log('Canvas approach failed, trying direct fetch...');
      try {
        const response = await fetch(imageUrl, { mode: 'cors' });
        if (response.ok) {
          blob = await response.blob();
        }
      } catch (fetchErr) {
        console.error('Direct fetch also failed:', fetchErr);
      }
    }

    if (!blob) {
      console.error('Could not load avatar image from:', imageUrl);
      return null;
    }

    // Determine file extension
    const extension = blob.type.split('/')[1] || 'jpg';
    const finalFilename = filename.includes('.') ? filename : `${filename}.${extension}`;

    // Upload to Supabase Storage
    const filePath = `${userId}/${finalFilename}`;
    const { data, error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .upload(filePath, blob, {
        contentType: blob.type || 'image/jpeg',
        cacheControl: '3600',
        upsert: true, // Replace if exists
      });

    if (error) {
      console.error('Failed to upload avatar to Supabase:', error);
      return null;
    }

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from(AVATAR_BUCKET)
      .getPublicUrl(data.path);

    console.log('Avatar uploaded successfully:', publicUrl);
    return publicUrl;
  } catch (err) {
    console.error('Error downloading/storing avatar:', err);
    return null;
  }
}

/**
 * Download Google profile avatar and store it in Supabase Storage
 * @param userId - The user's ID
 * @param googleAvatarUrl - The Google profile picture URL
 * @returns The Supabase Storage public URL, or null on failure
 */
export async function downloadAndStoreGoogleAvatar(
  userId: string,
  googleAvatarUrl: string
): Promise<string | null> {
  // Google avatar URLs often have size parameters - get a larger version
  let highResUrl = googleAvatarUrl;

  // If it's a Google user content URL, request a larger size
  if (googleAvatarUrl.includes('googleusercontent.com')) {
    // Remove any existing size parameter and add a larger one
    highResUrl = googleAvatarUrl.replace(/=s\d+/, '=s400');
    if (!highResUrl.includes('=s')) {
      highResUrl += '=s400';
    }
  }

  return downloadAndStoreAvatar(userId, highResUrl, 'google-avatar');
}

/**
 * Normalize Google avatar URL for comparison (remove size params)
 */
function normalizeGoogleAvatarUrl(url: string): string {
  if (!url) return '';
  // Remove size parameter for comparison
  return url.replace(/=s\d+(-c)?/, '').replace(/\?.*$/, '');
}

/**
 * Check if Google avatar has changed and needs sync
 * @param currentGoogleUrl - The current Google avatar URL from OAuth metadata
 * @param storedGoogleSource - The previously stored Google source URL
 * @returns true if the avatar has changed
 */
export function hasGoogleAvatarChanged(
  currentGoogleUrl: string | null | undefined,
  storedGoogleSource: string | null | undefined
): boolean {
  if (!currentGoogleUrl) return false;
  if (!storedGoogleSource) return true; // Never synced before

  // Compare normalized URLs (without size params)
  const normalizedCurrent = normalizeGoogleAvatarUrl(currentGoogleUrl);
  const normalizedStored = normalizeGoogleAvatarUrl(storedGoogleSource);

  return normalizedCurrent !== normalizedStored;
}

/**
 * Sync Google avatar using Edge Function (server-side, no CORS issues)
 * Only downloads if the Google avatar has changed or was never synced
 * @param userId - The user's ID
 * @param googleAvatarUrl - Current Google avatar URL from OAuth
 * @param storedGoogleSource - Previously stored Google source URL (from preferences)
 * @param currentAvatarUrl - Current avatar_url in profile (might be bucket URL)
 * @returns Object with stored URL and source, or null if no sync needed/failed
 */
export async function syncGoogleAvatar(
  userId: string,
  googleAvatarUrl: string | null | undefined,
  storedGoogleSource: string | null | undefined,
  currentAvatarUrl: string | null | undefined
): Promise<{ avatarUrl: string; googleSource: string } | null> {
  // No Google avatar to sync
  if (!googleAvatarUrl) {
    return null;
  }

  // Quick client-side check to avoid unnecessary edge function calls
  const hasChanged = hasGoogleAvatarChanged(googleAvatarUrl, storedGoogleSource);
  if (!hasChanged && currentAvatarUrl && currentAvatarUrl.includes('supabase')) {
    console.log('Google avatar unchanged, using cached version');
    return null;
  }

  console.log('Syncing Google avatar via edge function...');

  try {
    // Call the edge function
    const { data, error } = await supabase.functions.invoke('sync-google-avatar', {
      body: {
        googleAvatarUrl,
        userId,
        storedGoogleSource,
        currentAvatarUrl,
      },
    });

    if (error) {
      console.error('Edge function error:', error);
      // Fall back to client-side sync
      return await syncGoogleAvatarClientSide(userId, googleAvatarUrl);
    }

    if (data?.skipped) {
      console.log('Edge function: sync skipped (no changes)');
      return null;
    }

    if (data?.success && data.avatarUrl) {
      console.log('Google avatar synced successfully via edge function');
      return {
        avatarUrl: data.avatarUrl,
        googleSource: data.googleSource || googleAvatarUrl,
      };
    }

    // If edge function failed, try client-side fallback
    console.warn('Edge function returned unsuccessful, trying client-side fallback');
    return await syncGoogleAvatarClientSide(userId, googleAvatarUrl);
  } catch (err) {
    console.error('Failed to sync Google avatar:', err);
    // Fall back to client-side sync
    return await syncGoogleAvatarClientSide(userId, googleAvatarUrl);
  }
}

/**
 * Client-side fallback for syncing Google avatar
 * Uses canvas approach to handle CORS
 */
async function syncGoogleAvatarClientSide(
  userId: string,
  googleAvatarUrl: string
): Promise<{ avatarUrl: string; googleSource: string } | null> {
  console.log('Attempting client-side Google avatar sync...');

  try {
    const storedUrl = await downloadAndStoreGoogleAvatar(userId, googleAvatarUrl);

    if (storedUrl) {
      console.log('Google avatar synced successfully (client-side)');
      return {
        avatarUrl: storedUrl,
        googleSource: googleAvatarUrl,
      };
    }
  } catch (err) {
    console.error('Client-side sync also failed:', err);
  }

  return null;
}

/**
 * Delete a user's avatar from storage
 * @param userId - The user's ID
 * @param filename - The filename to delete
 */
export async function deleteAvatar(
  userId: string,
  filename: string = 'avatar.jpg'
): Promise<boolean> {
  try {
    const filePath = `${userId}/${filename}`;
    const { error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Failed to delete avatar:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error deleting avatar:', err);
    return false;
  }
}

/**
 * Get the public URL for a user's avatar
 * @param userId - The user's ID
 * @param filename - The filename
 */
export function getAvatarUrl(userId: string, filename: string = 'avatar.jpg'): string {
  const filePath = `${userId}/${filename}`;
  const { data: { publicUrl } } = supabase.storage
    .from(AVATAR_BUCKET)
    .getPublicUrl(filePath);
  return publicUrl;
}

/**
 * Check if an avatar exists in storage
 * @param userId - The user's ID
 * @param filename - The filename to check
 */
export async function avatarExists(
  userId: string,
  filename: string = 'avatar.jpg'
): Promise<boolean> {
  try {
    const { data, error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .list(userId, {
        search: filename,
      });

    if (error) {
      console.error('Error checking avatar:', error);
      return false;
    }

    return data.some(file => file.name === filename);
  } catch (err) {
    console.error('Error checking avatar:', err);
    return false;
  }
}

/**
 * Download an image from a URL and store it in Supabase Storage for icons
 * Similar to avatar handling but for list icons and other custom icons
 * @param householdId - The household ID for organizing storage
 * @param imageUrl - The source URL to download
 * @param prefix - Path prefix within the household folder (e.g., 'list-icons')
 * @returns Object with stored URL and original source, or null on failure
 */
export async function downloadAndStoreIcon(
  householdId: string,
  imageUrl: string,
  prefix: string = 'list-icons'
): Promise<{ storedUrl: string; sourceUrl: string } | null> {
  try {
    // Try canvas approach first (handles CORS better)
    let blob = await loadImageAsBlob(imageUrl);

    // If canvas fails, try direct fetch as fallback
    if (!blob) {
      console.log('Canvas approach failed for icon, trying direct fetch...');
      try {
        const response = await fetch(imageUrl, { mode: 'cors' });
        if (response.ok) {
          blob = await response.blob();
        }
      } catch (fetchErr) {
        console.error('Direct fetch also failed:', fetchErr);
      }
    }

    if (!blob) {
      console.error('Could not load icon image from:', imageUrl);
      return null;
    }

    // Determine file extension
    const extension = blob.type.split('/')[1] || 'jpg';
    const filename = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${extension}`;
    const filePath = `${householdId}/${prefix}/${filename}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .upload(filePath, blob, {
        contentType: blob.type || 'image/jpeg',
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('Failed to upload icon to Supabase:', error);
      return null;
    }

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from(AVATAR_BUCKET)
      .getPublicUrl(data.path);

    console.log('Icon uploaded successfully:', publicUrl);
    return {
      storedUrl: publicUrl,
      sourceUrl: imageUrl,
    };
  } catch (err) {
    console.error('Error downloading/storing icon:', err);
    return null;
  }
}
